/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */
import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {fromJS, List, Map} from 'immutable';
import moment from 'moment';
import classnames from 'classnames';
import {camelCase} from 'lodash';

import {pluralize} from 'lib/stringUtils';
import {Banner, Button, Heading, Text} from '@albert-io/atomic';

import AssignmentCard from './AssignmentCard/AssignmentCard.react';

import './assignments-list.scss';
import placeholderImg from './practice.svg';

/**
 * @typedef {import('../../resources/augmented/Assignment/AssignmentModel.v3').AssignmentModelV3} AssignmentModelV3
 */

/**
 * Returns -1 (or any negative number) if prev comes before next
 * Returns 1 (or any positive number) if prev comes after next
 *
 * @param {AssignmentModelV3} prev - the first value provided to our comparator
 * @param {AssignmentModelV3} next - the second value provided to our comparator
 *
 * @returns {number} -1 or 1
 */
function sortAssignmentsByDueDate(prev, next, studentId) {
  const prevClassroom = prev.getClassroomWithLatestAssignmentDueDate();
  const nextClassroom = next.getClassroomWithLatestAssignmentDueDate();
  const timeDifference = moment(
    prev.getDueDateFromSettings({
      studentId,
      classroomId: prevClassroom?.getId?.()
    })
  ).diff(
    moment(
      next.getDueDateFromSettings({
        studentId,
        classroomId: nextClassroom?.getId?.()
      })
    )
  );
  if (timeDifference === 0) {
    const isPrevGreater = prev.getName() > next.getName();
    if (isPrevGreater) {
      return 1;
    }
    return -1;
  }
  return timeDifference;
}

export const dueDateTimeframes = {
  pastDue: 'Past Due',
  dueToday: 'Due Today',
  dueThisWeek: 'Due This Week',
  dueLater: 'Due Later'
};

/**
 * @param {List<AssignmentModelV3>} assignments - A student's open assignments up to a week from now
 * @returns {Map<string, List<AssignmentModelV3>>} structured as Immutable Map([stringified timeframe]: [List of Assignments])
 */
function groupAssignmentsByTimeframe(assignments, studentId) {
  const {pastDue, dueToday, dueThisWeek, dueLater} = dueDateTimeframes;
  const sortedAssignments = assignments
    .sort((prev, next) => sortAssignmentsByDueDate(prev, next, studentId))
    .reduce(
      (prev, curr) => {
        const classroom = curr.getClassroomWithLatestAssignmentDueDate();
        const classroomId = classroom?.getId?.();
        const idsToSelect = {
          classroomId,
          studentId
        };
        if (curr.isOverDue(idsToSelect)) {
          return prev;
        }
        if (curr.isPastDue(idsToSelect)) {
          return prev.set(pastDue, prev.get(pastDue).push(curr));
        }
        if (curr.isDueIn1Day(idsToSelect)) {
          return prev.set(dueToday, prev.get(dueToday).push(curr));
        }
        if (curr.isDueIn1Week(idsToSelect)) {
          return prev.set(dueThisWeek, prev.get(dueThisWeek).push(curr));
        }
        return prev.set(dueLater, prev.get(dueLater).push(curr));
      },
      fromJS({
        [pastDue]: [],
        [dueToday]: [],
        [dueThisWeek]: [],
        [dueLater]: []
      })
    );
  return sortedAssignments;
}

const NoAssignmentsPlaceholder = () => (
  <div className='assignments-list__placeholder'>
    <img alt='' className='assignments-list__placeholder__img' src={placeholderImg} width='48' />
    <Text italic emphasis='lowest' size='regular'>
      No assignments due
    </Text>
  </div>
);

const PAST_DUE_CAP = 25;

const AssignmentsList = ({assignments = List(), className, studentId}) => {
  const [showPastDue, setShowPastDue] = useState(false);

  /* Only show the 25 most recent past due assignments */
  /* @todo: outsource this to Mandark */
  const [cappedAssignments, totalPastDue] = useMemo(
    () =>
      assignments.reduce(
        ([prev, pastDueCount], curr) => {
          const classroom = curr.getClassroomWithLatestAssignmentDueDate();
          const idsToSelect = {
            studentId,
            classroomId: classroom?.getId?.()
          };
          if (
            !curr.isOverDue(idsToSelect) &&
            curr.isPastDue(idsToSelect) &&
            pastDueCount < PAST_DUE_CAP
          ) {
            pastDueCount += 1;
            return [prev.push(curr), pastDueCount];
          }
          if (!curr.isOverDue(idsToSelect) && curr.isPastDue(idsToSelect)) {
            pastDueCount += 1;
            return [prev, pastDueCount];
          }
          if (!curr.isOverDue(idsToSelect)) {
            return [prev.push(curr), pastDueCount];
          }
          return [prev, pastDueCount];
        },
        [List(), 0]
      ),
    [assignments, studentId]
  );

  const sortedAssignments = useMemo(
    () => groupAssignmentsByTimeframe(cappedAssignments, studentId),
    [cappedAssignments, studentId]
  );

  /* Groups assignments under their respective headers: 'Due today', 'Due tomorrow'. etc */
  const filteredTimeframes = sortedAssignments
    .keySeq()
    .filter((key) => sortedAssignments.get(key).size);

  if (filteredTimeframes.isEmpty()) {
    return (
      <aside className={className}>
        <Heading as='h2' className='assignments-list__heading u-mar-b_3' size='l'>
          Assignments
        </Heading>
        <NoAssignmentsPlaceholder />
      </aside>
    );
  }

  const totalPastDueAssignments = totalPastDue > 25 ? '25+' : totalPastDue;
  return (
    <aside className={className}>
      <div className='assignments-list__heading-wrapper'>
        <Heading as='h2' className='assignments-list__heading u-mar-b_0' size='l'>
          Assignments
        </Heading>
      </div>
      {totalPastDueAssignments ? (
        <Banner className='u-mar-b_3 assignments-list__overdue-banner' theme='danger'>
          <div className='u-display_flex u-align-items_center u-width_100pc'>
            <div className='u-mar-r_auto'>
              {`${totalPastDueAssignments} past due ${pluralize('assignment', totalPastDue)}`}
            </div>
            <Button
              variant='text'
              color='destructive'
              onClick={() => {
                setShowPastDue(!showPastDue);
              }}
              size='s'
              type='button'
            >
              {showPastDue ? 'Hide' : 'Show'}
            </Button>
          </div>
        </Banner>
      ) : null}
      <div className='assignments-list__carousel' aria-label='Your Assignments'>
        {filteredTimeframes
          .filter((key) => (showPastDue ? key : key !== dueDateTimeframes.pastDue))
          .map((key) => {
            const id = camelCase(key);
            return (
              <div className='assignments-list__carousel__group'>
                <Heading
                  as='h3'
                  className='assignments-list__carousel__group__heading'
                  id={id}
                  size='xxs'
                >
                  {key}
                </Heading>
                <ul
                  aria-labelledby={id}
                  className='u-list-style_none u-mar_0 u-pad_0 u-display_flex assignments-list__list-group'
                >
                  {sortedAssignments.get(key).map((assignment) => {
                    return (
                      <li
                        className={classnames('assignments-list__list-group__item')}
                        key={assignment.getId()}
                      >
                        <AssignmentCard
                          assignment={assignment}
                          className='u-width_100pc'
                          studentId={studentId}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
      </div>
    </aside>
  );
};

AssignmentsList.propTypes = {
  assignments: PropTypes.array,
  className: PropTypes.string,
  studentId: PropTypes.string
};

export default AssignmentsList;
